import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Tooltip,
  Zoom,
  Fade,
  Alert,
  TextField
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import { coverPhotoAPI } from '../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const CoverPhotoApproval = () => {
  const [coverPhotos, setCoverPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [total, setTotal] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCoverPhotos = async () => {
    setLoading(true);
    try {
      // Map tab to status
      const statusMap = ['pending', 'approved', 'rejected'];
      const status = statusMap[tabValue];
      
      const response = await coverPhotoAPI.getCoverPhotos({
        page: page + 1,
        limit: rowsPerPage,
        status: status
      });
      setCoverPhotos(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      toast.error('Failed to fetch cover photos');
      console.error('Error fetching cover photos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverPhotos();
  }, [page, rowsPerPage, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleApprove = async (photo) => {
    setActionLoading(true);
    try {
      await coverPhotoAPI.approveCoverPhoto(photo.user_id);
      toast.success('Cover photo approved successfully');
      fetchCoverPhotos();
    } catch (error) {
      toast.error('Failed to approve cover photo');
      console.error('Error approving cover photo:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPhoto) return;
    setActionLoading(true);
    try {
      await coverPhotoAPI.rejectCoverPhoto(selectedPhoto.user_id, { 
        reason: rejectReason || 'Inappropriate content' 
      });
      toast.success('Cover photo rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedPhoto(null);
      fetchCoverPhotos();
    } catch (error) {
      toast.error('Failed to reject cover photo');
      console.error('Error rejecting cover photo:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePreview = (photo) => {
    setSelectedPhoto(photo);
    setPreviewDialogOpen(true);
  };

  // Updated function to handle numeric status values
  const getStatusChip = (photo) => {
    // Use the status from the backend (now properly mapped)
    const status = photo.is_cover_verified_status || 'pending';
    
    switch(status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'approved':
        return <Chip label="Approved" color="success" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  // Helper to format cover photo URL
  const getCoverPhotoUrl = (photo) => {
    if (!photo.cover_image_url) return '';
    // If it's a full URL, use it directly
    if (photo.cover_image_url.startsWith('http')) {
      return photo.cover_image_url;
    }
    // Otherwise, prepend the base URL
    return `${process.env.REACT_APP_API_BASE_URL || ''}${photo.cover_image_url}`;
  };

  // Check if photo is pending (numeric 0 or null)
  const isPending = (photo) => {
    return photo.is_cover_verified === 0 || photo.is_cover_verified === null || photo.is_cover_verified === undefined;
  };

  // Check if photo is approved (numeric 1)
  const isApproved = (photo) => {
    return photo.is_cover_verified === 1;
  };

  // Check if photo is rejected (numeric 2)
  const isRejected = (photo) => {
    return photo.is_cover_verified === 2;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Cover Photo Approval
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchCoverPhotos}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Pending" />
          <Tab label="Approved" />
          <Tab label="Rejected" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : coverPhotos.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PhotoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No cover photos to display
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {tabValue === 0 ? 'All cover photos have been reviewed' : 
             tabValue === 1 ? 'No approved cover photos found' : 
             'No rejected cover photos found'}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Cover Photo</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coverPhotos.map((photo) => (
                  <TableRow key={photo.id}>
                    <TableCell>{photo.id}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={photo.profile_photo_url} sx={{ width: 32, height: 32 }}>
                          {photo.first_name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {photo.first_name} {photo.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {photo.user_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box 
                        component="img"
                        src={getCoverPhotoUrl(photo)}
                        alt="Cover"
                        sx={{
                          width: 120,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        onClick={() => handlePreview(photo)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<Typography variant="caption" color="error">Failed to load</Typography>';
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {moment(photo.cover_uploaded_at || photo.created_at).format('YYYY-MM-DD HH:mm')}
                    </TableCell>
                    <TableCell>{getStatusChip(photo)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Preview" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(photo)}
                            color="info"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {isPending(photo) && (
                          <>
                            <Tooltip title="Approve" arrow>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(photo)}
                                disabled={actionLoading}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedPhoto(photo);
                                  setRejectDialogOpen(true);
                                }}
                                disabled={actionLoading}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[6, 12, 24, 48]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
      >
        <DialogTitle>
          Cover Photo Preview
          {selectedPhoto && (
            <Box component="span" ml={2}>
              {getStatusChip(selectedPhoto)}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  User: {selectedPhoto.first_name} {selectedPhoto.last_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Uploaded: {moment(selectedPhoto.cover_uploaded_at || selectedPhoto.created_at).format('YYYY-MM-DD HH:mm:ss')}
                </Typography>
              </Box>
              <Box
                component="img"
                src={getCoverPhotoUrl(selectedPhoto)}
                alt="Cover Photo Preview"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: 500,
                  objectFit: 'contain',
                  borderRadius: 2,
                  bgcolor: '#f5f5f5'
                }}
                onError={(e) => {
                  e.target.src = '/placeholder-cover.jpg';
                  e.target.alt = 'Failed to load image';
                }}
              />
              {isRejected(selectedPhoto) && selectedPhoto.cover_rejection_reason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Rejection Reason: {selectedPhoto.cover_rejection_reason}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPhoto && isPending(selectedPhoto) && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleApprove(selectedPhoto);
                  setPreviewDialogOpen(false);
                }}
                disabled={actionLoading}
                startIcon={<ApproveIcon />}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setPreviewDialogOpen(false);
                  setRejectDialogOpen(true);
                }}
                disabled={actionLoading}
                startIcon={<RejectIcon />}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectReason('');
          setSelectedPhoto(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Cover Photo</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejecting this cover photo. This will be visible to the user.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            placeholder="Enter reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            variant="outlined"
          />
          {selectedPhoto && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }} color="textSecondary">
              User: {selectedPhoto.first_name} {selectedPhoto.last_name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setRejectReason('');
            setSelectedPhoto(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={actionLoading}
            startIcon={<RejectIcon />}
          >
            Reject Photo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoverPhotoApproval;